/**
 * useFolderCreateGate · 手动创建文件夹的全局数量卡点
 *
 * 由 AI 智能分类的 useSingleCreateGate 升格而来：普通文件夹与 AI 智能分类
 * 文件夹共用同一份团队配额（folderQuotaSlice 接口4 used/cap/tiers，两类都计数），
 * 卡点对所有"显性手动创建文件夹"入口生效：
 *  · FoldersPicker（添加/移动到文件夹弹窗的内联新建，组件内置本卡点）
 *  · photoFolders 列表页 / 详情页新建按钮
 *  · AI 智能分类列表页新建 / 详情页新建子文件夹
 *
 * 返回 gate 函数：已达上限 → 拉 VipPaymentModal 开通弹窗（单个创建动态文案）
 * 并返回 false（调用方拦截新建流程）；额度够 → 返回 true 放行。
 *
 * 数据自取：hook 内部在配额未加载或团队变化时拉一次接口4，消费方无需关心
 * 数据时机。fail-open：配额未就绪/拉取失败一律放行，超限由后端创建接口兜底
 * ——卡点是商业化引导，不因配额接口抖动阻断正常创建。
 */
import { useCallback, useEffect } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVipKind
} from '../../../app/hooks'
import {
  fetchFolderQuota,
  selectFolderQuota
} from '../../../slices/folderQuotaSlice'
import { deriveSingleCreatePaywall } from './paywall'
import { singleModalData } from './paywallModalData'
import { folderPaywallCustomContent } from './paywallModalContent'

export function useFolderCreateGate(): () => boolean {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s) => (s.group as any).groupId)
  const vipKind = useVipKind()
  const quota = useAppSelector(selectFolderQuota)
  const payment = usePayment()

  // 配额自取：未加载过 / 切团队后重拉一次；loading 防并发，失败后 groupID
  // 也会写入（见 slice rejected 分支），不会反复重试
  useEffect(() => {
    if (!groupID || quota.loading) return
    if (quota.loaded && quota.groupID === String(groupID)) return
    dispatch(fetchFolderQuota({ groupID: String(groupID) }))
  }, [groupID, quota.loading, quota.loaded, quota.groupID, dispatch])

  return useCallback(() => {
    // fail-open：配额未就绪 / 拉取失败 / 数据不属于当前团队 → 放行
    if (
      !quota.loaded ||
      quota.error ||
      quota.groupID !== String(groupID || '')
    ) {
      return true
    }
    const gate = deriveSingleCreatePaywall({
      vipKind,
      used: quota.used,
      cap: quota.cap,
      tiers: quota.tiers
    })
    if (gate.blocked) {
      // 开通弹窗动态文案：单个创建（single）—— PRD §3.4
      payment.openVipByKind(
        gate.upgradeTargetVipKind,
        gate.sourceDetail,
        false,
        folderPaywallCustomContent(
          singleModalData({
            vipKind,
            used: quota.used,
            cap: quota.cap,
            tiers: quota.tiers
          })
        )
      )
      return false
    }
    return true
  }, [
    vipKind,
    groupID,
    quota.loaded,
    quota.error,
    quota.groupID,
    quota.used,
    quota.cap,
    quota.tiers,
    payment
  ])
}
